/**
 * RoomDetailPage — drill-in from the Room Readiness Board.
 *
 * Read-only visibility surface for a single room: hierarchy, current
 * status, active cleaning task (if any), open maintenance issues, and
 * the recent status-change history. Mirrors the board's stance that
 * mutations never originate here — transitions still go through the
 * room-transition Edge Function via the task detail surface or the
 * supervisor inspection queue.
 *
 * Active-task link routes to `/app/tasks/:id` so the supervisor can
 * step from "what's this room's state" into the existing task surface
 * with the inspection / approval controls.
 */

import { useMemo, type ReactNode } from 'react'
import { Link, useParams }          from 'react-router'
import { useTranslation }           from 'react-i18next'
import {
  useRoomDetail,
  type RoomDetailHistoryEntry,
  type RoomDetailRoom,
  type RoomDetailActiveTask,
} from '@/hooks/useRoomDetail'
import { taskDetailPath }                  from '@/router/paths'
import type { RoomStatus }                  from '@/hooks/useRoomBoard'
import type { MaintenanceIssueRow }         from '@/hooks/useMaintenanceIssues'

export function RoomDetailPage(): ReactNode {
  const { t }      = useTranslation()
  const { roomId } = useParams<{ roomId: string }>()
  const { data, isLoading, error } = useRoomDetail(roomId)

  // Sort history newest-first regardless of server order; useful as a
  // belt-and-suspenders if the underlying refetchInterval ever returns
  // an out-of-order page (PostgREST honors order, but defending the
  // invariant in one place is cheap).
  const history = useMemo<RoomDetailHistoryEntry[]>(
    () => (data?.history ?? []).slice().sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [data?.history],
  )

  if (isLoading) {
    return <Hint>{t('roomDetail.loading', 'Loading room…')}</Hint>
  }
  if (error) {
    return (
      <ErrorPanel
        message={
          error instanceof Error
            ? error.message
            : t('roomDetail.failedToLoad', 'Failed to load the room.')
        }
      />
    )
  }
  if (!data) {
    return <ErrorPanel message={t('roomDetail.notFound', 'Room not found.')} />
  }

  return (
    <article style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <RoomHeader room={data.room} />
      <StatusCard room={data.room} />
      <ActiveTaskCard task={data.task} />
      <MaintenanceIssuesCard issues={data.issues} />
      <HistoryCard history={history} />
    </article>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Subsections — kept inline so the page is one file (under 600 lines).
// ─────────────────────────────────────────────────────────────────────

function RoomHeader({ room }: { room: RoomDetailRoom }): ReactNode {
  const { t } = useTranslation()
  const parts: string[] = []
  if (room.campus)   parts.push(room.campus.name)
  if (room.building) parts.push(room.building.name)
  if (room.floor)    parts.push(room.floor.label)

  return (
    <header>
      <p style={{ margin: 0, color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
        {parts.length === 0
          ? t('roomDetail.unknownLocation', 'Location unknown')
          : parts.join(' · ')}
      </p>
      <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
        {t('roomDetail.title', 'Room {{number}}', { number: room.room_number })}
      </h1>
      <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
        {room.room_type ? room.room_type.name : t('roomDetail.unknownType', 'Unknown type')}
        {' · '}
        {t('roomDetail.bedCount', '{{count}} bed', { count: room.bed_count })}
      </p>
    </header>
  )
}

function StatusCard({ room }: { room: RoomDetailRoom }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      data-testid="status-card"
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        display:      'flex',
        alignItems:   'center',
        gap:          '0.75rem',
      }}
    >
      <strong style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
        {t('roomDetail.statusLabel', 'Current status')}
      </strong>
      <StatusChip status={room.current_status} />
    </section>
  )
}

function ActiveTaskCard({ task }: { task: RoomDetailActiveTask | null }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      aria-labelledby="active-task-heading"
      data-testid="active-task-card"
      style={cardStyle}
    >
      <h2 id="active-task-heading" style={cardHeadingStyle}>
        {t('roomDetail.activeTask', 'Active cleaning task')}
      </h2>
      {!task ? (
        <p style={emptyHintStyle}>
          {t('roomDetail.noActiveTask', 'No active cleaning task on this room.')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <p style={{ margin: 0 }}>
            <strong>{t('roomDetail.taskStatusLabel', 'Status')}:</strong>{' '}
            {t(`taskStatus.${task.status}`, task.status)}
            {' · '}
            {t('roomDetail.taskCycleLabel', 'Cycle')}: {task.cycle_type}
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--brand-neutral-500)' }}>
            {task.assignee?.full_name
              ? t('roomDetail.taskAssignee', 'Assigned to {{name}}', { name: task.assignee.full_name })
              : t('roomDetail.taskUnassigned', 'Unassigned')}
            {task.started_at && (
              <> · {t('roomDetail.taskStartedAt', 'Started {{when}}', { when: formatWhen(task.started_at) })}</>
            )}
            {task.completed_at && (
              <> · {t('roomDetail.taskSubmittedAt', 'Submitted {{when}}', { when: formatWhen(task.completed_at) })}</>
            )}
          </p>
          <p style={{ margin: '0.25rem 0 0' }}>
            <Link
              data-testid="active-task-link"
              to={taskDetailPath(task.id)}
              style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
            >
              {t('roomDetail.openTask', 'Open task →')}
            </Link>
          </p>
        </div>
      )}
    </section>
  )
}

function MaintenanceIssuesCard({ issues }: { issues: MaintenanceIssueRow[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      aria-labelledby="issues-heading"
      data-testid="issues-card"
      style={cardStyle}
    >
      <h2 id="issues-heading" style={cardHeadingStyle}>
        {t('roomDetail.maintenanceIssues', 'Open maintenance issues')}
      </h2>
      {issues.length === 0 ? (
        <p style={emptyHintStyle}>
          {t('roomDetail.noIssues', 'No open maintenance issues.')}
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {issues.map((i) => (
            <li
              key={i.id}
              data-testid={`issue-${i.id}`}
              style={{
                border:       '1px solid var(--brand-neutral-300)',
                borderLeft:   `3px solid ${severityColor(i.severity)}`,
                borderRadius: 'var(--brand-radius-sm)',
                padding:      '0.5rem 0.75rem',
              }}
            >
              <p style={{ margin: 0, fontWeight: 600 }}>
                {i.title}
                {i.blocks_readiness && (
                  <span
                    style={{
                      marginLeft:    '0.5rem',
                      fontSize:      '0.7rem',
                      padding:       '0.05rem 0.4rem',
                      borderRadius:  'var(--brand-radius-full)',
                      background:    'var(--brand-danger-subtle)',
                      color:         'var(--brand-danger)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {t('roomDetail.blocksReadiness', 'Blocks ready')}
                  </span>
                )}
              </p>
              <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: 'var(--brand-neutral-500)' }}>
                {t(`severity.${i.severity}`, i.severity)}
                {' · '}
                {t(`maintenanceIssueStatus.${i.status}`, i.status)}
                {' · '}
                {t('roomDetail.openedAt', 'Opened {{when}}', { when: formatWhen(i.opened_at) })}
              </p>
              {i.description && (
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem' }}>{i.description}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function HistoryCard({ history }: { history: RoomDetailHistoryEntry[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      aria-labelledby="history-heading"
      data-testid="history-card"
      style={cardStyle}
    >
      <h2 id="history-heading" style={cardHeadingStyle}>
        {t('roomDetail.history', 'Recent status changes')}
      </h2>
      {history.length === 0 ? (
        <p style={emptyHintStyle}>
          {t('roomDetail.noHistory', 'No recorded status changes yet.')}
        </p>
      ) : (
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {history.map((h) => (
            <li
              key={h.id}
              data-testid={`history-${h.id}`}
              style={{
                fontSize:    '0.85rem',
                paddingLeft: '0.6rem',
                borderLeft:  '2px solid var(--brand-neutral-300)',
              }}
            >
              <p style={{ margin: 0 }}>
                {h.previous_status
                  ? t('roomDetail.historyTransition', '{{from}} → {{to}}', {
                      from: t(`status.${h.previous_status}`, h.previous_status.replace(/_/g, ' ')),
                      to:   t(`status.${h.new_status}`,      h.new_status.replace(/_/g, ' ')),
                    })
                  : t('roomDetail.historyInitial', 'Created as {{to}}', {
                      to: t(`status.${h.new_status}`, h.new_status.replace(/_/g, ' ')),
                    })}
              </p>
              <p style={{ margin: 0, color: 'var(--brand-neutral-500)' }}>
                {formatWhen(h.created_at)}
                {h.changed_by?.full_name && <> · {h.changed_by.full_name}</>}
                {h.reason && <> · {h.reason}</>}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Style + helper bits
// ─────────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background:   'var(--brand-white)',
  border:       '1px solid var(--brand-neutral-300)',
  borderRadius: 'var(--brand-radius-md)',
  padding:      '0.75rem 1rem',
}

const cardHeadingStyle: React.CSSProperties = {
  margin:     '0 0 0.5rem',
  fontSize:   '1rem',
  fontFamily: 'var(--brand-font-family-display)',
}

const emptyHintStyle: React.CSSProperties = {
  margin: 0,
  color:  'var(--brand-neutral-500)',
  fontSize: '0.9rem',
}

function StatusChip({ status }: { status: RoomStatus }): ReactNode {
  const { t } = useTranslation()
  return (
    <span
      data-testid="status-chip"
      data-status={status}
      style={{
        fontSize:      '0.8rem',
        padding:       '0.15rem 0.55rem',
        borderRadius:  'var(--brand-radius-full)',
        background:    statusBg(status),
        color:         statusFg(status),
        textTransform: 'capitalize',
      }}
    >
      {t(`status.${status}`, status.replace(/_/g, ' '))}
    </span>
  )
}

function statusBg(s: RoomStatus): string {
  switch (s) {
    case 'maintenance_required':
    case 'inspection_pending':   return 'var(--brand-danger-subtle)'
    case 'ready':
    case 'occupied':             return 'var(--brand-success-subtle)'
    default:                     return 'var(--brand-primary-subtle)'
  }
}

function statusFg(s: RoomStatus): string {
  switch (s) {
    case 'maintenance_required':
    case 'inspection_pending':   return 'var(--brand-danger)'
    case 'ready':
    case 'occupied':             return 'var(--brand-success)'
    default:                     return 'var(--brand-primary)'
  }
}

function severityColor(s: MaintenanceIssueRow['severity']): string {
  switch (s) {
    case 'critical': return 'var(--brand-danger)'
    case 'high':     return 'var(--brand-danger)'
    case 'medium':   return 'var(--brand-primary)'
    case 'low':      return 'var(--brand-neutral-500)'
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}
